/**
 * Org-scoped store reset registry.
 *
 * `switchOrg` swaps the active organization WITHOUT a page reload, so any
 * feature store that caches org-scoped data (accounts, campaigns, counts…)
 * would keep showing the PREVIOUS org's data until a hard refresh. Feature
 * stores register a reset callback here at module init; AuthProvider calls
 * `resetOrgScopedStores()` whenever the active organization id changes, so
 * every registered cache clears + refetches for the new org.
 *
 * Register liberally — any zustand store / module-level cache keyed by the
 * current org belongs here.
 */

type ResetFn = () => void;

const resets = new Set<ResetFn>();

/** Register a reset callback. Returns an unregister function (rarely needed). */
export function registerOrgScopedReset(fn: ResetFn): () => void {
    resets.add(fn);
    return () => resets.delete(fn);
}

/** Reset every registered org-scoped cache. Never throws (one bad reset must not block the rest). */
export function resetOrgScopedStores(): void {
    resets.forEach((fn) => {
        try {
            fn();
        } catch (err) {
            console.error('[orgScopedReset] reset callback failed:', err);
        }
    });
}
